import { useEffect, useMemo, useState } from "react";
import { Alert, AutoComplete, Form, Modal, Spin } from "antd";
import { toast } from "react-toastify";
import { getParametre, updateMotifReglement } from "../../../../hooks/useHooks";
import { PARAMETERS } from "../../../../variables/constants";

/**
 * Modification du SEUL motif d'un règlement (décaissement) déjà VALIDE.
 * Réservé aux profils habilités (HSI089) — l'action n'est proposée que sous
 * habilitation côté liste, et le backend revérifie l'habilitation + l'état VALIDE.
 */
const EditMotifReglementModal = ({ open, decaissement, onClose, onSaved }) => {
    const [form] = Form.useForm();
    const [motifs, setMotifs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const currentMotif = decaissement?.motifReglement || "";

    useEffect(() => {
        if (!open) return;
        form.setFieldsValue({ motifReglement: currentMotif || undefined });
        setLoading(true);
        getParametre(PARAMETERS.motifReglement)
            .then(({ data }) => setMotifs((data || []).map((m) => ({ label: m.valeur, value: m.valeur }))))
            .catch(() => setMotifs([]))
            .finally(() => setLoading(false));
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // Garantit que le motif actuel reste sélectionnable même s'il n'est plus dans la liste paramétrée.
    const options = useMemo(() => {
        // Dé-duplication par valeur (évite les répétitions dans la liste déroulante).
        const seen = new Set();
        const base = [];
        motifs.forEach((o) => {
            const key = String(o?.value ?? "").trim().toLowerCase();
            if (!key || seen.has(key)) return;
            seen.add(key);
            base.push(o);
        });
        if (currentMotif && !base.some((o) => o.value === currentMotif)) {
            base.unshift({ label: currentMotif, value: currentMotif });
        }
        return base;
    }, [motifs, currentMotif]);

    const handleOk = async () => {
        const { motifReglement } = await form.validateFields();
        if (motifReglement === currentMotif) { onClose?.(); return; }
        setSaving(true);
        try {
            await updateMotifReglement(decaissement.codeId, motifReglement);
            toast.success("Motif du règlement modifié.");
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.message || "Erreur lors de la modification du motif.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            title="Modifier le motif du règlement"
            onCancel={onClose}
            onOk={handleOk}
            confirmLoading={saving}
            okText="Enregistrer"
            cancelText="Annuler"
            destroyOnClose
        >
            <Alert
                type="info"
                showIcon
                message="Seul le motif est modifiable. Le montant, le bénéficiaire et l'état du règlement restent inchangés."
                style={{ marginBottom: 16 }}
            />
            <Spin spinning={loading}>
                <Form form={form} layout="vertical" requiredMark={false}>
                    <Form.Item
                        name="motifReglement"
                        label="Motif du règlement"
                        rules={[{ required: true, message: "Veuillez saisir un motif" }]}
                    >
                        <AutoComplete
                            options={options}
                            placeholder="Saisir un motif (ou choisir dans la liste)"
                            allowClear
                            style={{ width: "100%" }}
                            filterOption={(input, option) =>
                                (option?.value ?? "").toLowerCase().includes((input || "").toLowerCase())
                            }
                        />
                    </Form.Item>
                </Form>
            </Spin>
        </Modal>
    );
};

export default EditMotifReglementModal;
